"use client";

import { Typography } from "@health/ui";
import { Checkbox } from "@health/ui/components/checkbox";
import { WidgetProps } from "@rjsf/utils";

/**
 * Custom checkboxes widget that supports exclusive options.
 *
 * Usage in uiSchema:
 * {
 *   "field_name": {
 *     "ui:widget": "checkboxes",
 *     "ui:options": {
 *       "exclusiveItems": ["none", "no"]  // Items that act like radio buttons
 *     }
 *   }
 * }
 *
 * When an exclusive item is selected, all other items are deselected.
 * When any non-exclusive item is selected, exclusive items are deselected.
 */
export function CheckboxesWidget(props: WidgetProps) {
  const { id, value = [], options, onChange, disabled, readonly, schema } = props;

  const enumOptions = options.enumOptions || [];

  // Get exclusive items from uiSchema options
  const exclusiveItems: string[] = (options?.exclusiveItems as string[]) || [];

  const handleChange = (optionValue: string, checked: boolean) => {
    let newValue: string[];

    if (checked) {
      // Item is being checked
      if (exclusiveItems.includes(optionValue)) {
        // Exclusive item selected - clear everything else, only keep this
        newValue = [optionValue];
      } else {
        // Non-exclusive item selected - add it and remove any exclusive items
        newValue = [...value.filter((v: string) => !exclusiveItems.includes(v)), optionValue];
      }
    } else {
      // Item is being unchecked - simply remove it
      newValue = value.filter((v: string) => v !== optionValue);
    }

    onChange(newValue);
  };

  return (
    <div className="space-y-3">
      {schema.description && (
        <Typography.Hint className="mb-4">{schema.description}</Typography.Hint>
      )}
      <div className="space-y-0.5">
        {enumOptions.map((option: { value: string; label: string }) => {
          const isChecked = value.includes(option.value);
          const checkboxId = `${id}-${option.value}`;

          return (
            <label
              key={option.value}
              htmlFor={checkboxId}
              className={`flex items-center gap-3 min-h-[44px] py-2 transition-opacity text-accent group hover:text-black ${
                disabled || readonly ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <Checkbox
                id={checkboxId}
                checked={isChecked}
                onCheckedChange={(checked) => handleChange(option.value, checked === true)}
                disabled={disabled || readonly}
                className="rounded-full size-6 border-2 border-chips-border data-[state=checked]:bg-checkbox-filled data-[state=checked]:border-checkbox-filled transition-colors group-hover:border-primary/40"
              />
              <span className="flex-1 text-size-chips select-none">{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
